"use client";
import { useEffect, useRef, useState } from "react";

/**
 * Hook to trigger animations when an element scrolls into view
 * Uses Intersection Observer API for performance
 * 
 * @param options - Configuration options
 * @param options.threshold - Percentage of element visible before triggering (0-1, default: 0.1)
 * @param options.rootMargin - Margin around root for triggering (default: "0px 0px -100px 0px")
 * @param options.triggerOnce - Only trigger animation once (default: true)
 * @returns Tuple of [ref to attach to element, isVisible boolean]
 */
export function useScrollAnimation(
  options: {
    threshold?: number;
    rootMargin?: string;
    triggerOnce?: boolean;
  } = {}
) {
  const {
    threshold = 0.1,
    rootMargin = "0px 0px -100px 0px",
    triggerOnce = true,
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (triggerOnce) {
              observer.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [elementRef, isVisible] as const;
}

